"use client"

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import type { AIUseCase } from "@/lib/types"
import { ClassificationBadge, FeasibilityBadge, PriorityBadge, StatusBadge } from "./status-badges"
import { CalendarDays, Building2, User, Bot } from "lucide-react"

interface UseCaseDetailDrawerProps {
  useCase: AIUseCase | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <div className="text-sm text-foreground">{value}</div>
    </div>
  )
}

export function UseCaseDetailDrawer({ useCase, open, onOpenChange }: UseCaseDetailDrawerProps) {
  if (!useCase) return null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-[min(90vw,760px)] max-w-none overflow-y-auto px-6"
        style={{ width: "min(90vw, 760px)" }}
      >
        <SheetHeader className="pb-4 text-left">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-xs font-mono">{useCase.id}</Badge>
            <StatusBadge value={useCase.status} />
          </div>
          <SheetTitle className="text-lg leading-snug text-balance">{useCase.name}</SheetTitle>
          <SheetDescription className="text-sm">{useCase.businessProblem}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-5 mt-2">
          {/* Ownership */}
          <div className="grid grid-cols-2 gap-4 rounded-lg bg-muted/50 p-4">
            <DetailRow
              label="Department"
              value={
                <span className="flex items-center gap-1.5">
                  <Building2 className="size-3.5 text-muted-foreground" />
                  {useCase.department}
                </span>
              }
            />
            <DetailRow
              label="Business Owner"
              value={
                <span className="flex items-center gap-1.5">
                  <User className="size-3.5 text-muted-foreground" />
                  {useCase.businessOwner}
                </span>
              }
            />
            <DetailRow
              label="AI Owner"
              value={
                <span className="flex items-center gap-1.5">
                  <Bot className="size-3.5 text-muted-foreground" />
                  {useCase.aiOwner}
                </span>
              }
            />
            <DetailRow
              label="Created"
              value={
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="size-3.5 text-muted-foreground" />
                  {useCase.createdAt}
                </span>
              }
            />
          </div>

          <Separator />

          {/* Classification & Scoring */}
          <div className="flex flex-wrap gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Data Classification</span>
              <ClassificationBadge value={useCase.dataClassification} />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Feasibility</span>
              <FeasibilityBadge value={useCase.feasibilityFlag} />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Priority Band</span>
              <PriorityBadge value={useCase.priorityBand} />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Priority Score</span>
              <span className="text-2xl font-bold text-foreground">{useCase.priorityScore}</span>
            </div>
          </div>

          <Separator />

          <DetailRow label="Business Problem" value={useCase.businessProblem} />
          <DetailRow label="Current Workflow" value={useCase.currentWorkflow} />
          {useCase.candidateSolution && (
            <DetailRow label="Candidate AI Solution" value={useCase.candidateSolution} />
          )}
          <DetailRow label="Expected Benefit" value={useCase.expectedBenefit} />

          {(useCase.expectedTimeSaving || useCase.expectedCostSaving || useCase.expectedRiskReduction) && (
            <div className="grid grid-cols-3 gap-3">
              {useCase.expectedTimeSaving && (
                <div className="rounded-lg bg-muted/50 p-3 text-center">
                  <div className="text-xs text-muted-foreground mb-1">Time Saving</div>
                  <div className="text-sm font-semibold">{useCase.expectedTimeSaving}</div>
                </div>
              )}
              {useCase.expectedCostSaving && (
                <div className="rounded-lg bg-muted/50 p-3 text-center">
                  <div className="text-xs text-muted-foreground mb-1">Cost Saving</div>
                  <div className="text-sm font-semibold">{useCase.expectedCostSaving}</div>
                </div>
              )}
              {useCase.expectedRiskReduction && (
                <div className="rounded-lg bg-muted/50 p-3 text-center">
                  <div className="text-xs text-muted-foreground mb-1">Risk Reduction</div>
                  <div className="text-sm font-semibold">{useCase.expectedRiskReduction}</div>
                </div>
              )}
            </div>
          )}

          <Separator />

          <DetailRow
            label="Dependencies"
            value={
              <div className="flex flex-wrap gap-1.5 mt-0.5">
                {useCase.dependencies.map((dep) => (
                  <Badge key={dep} variant="secondary" className="text-xs">{dep}</Badge>
                ))}
              </div>
            }
          />

          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
            <span>Created: {useCase.createdAt}</span>
            <span>Last Updated: {useCase.updatedAt}</span>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
